import { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { X, Send, Loader2, Eye, ShoppingBasket, Trash2, Calculator, Check, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Base pricelist — [code, name, dimensions, cpm, category, preview_config]
// preview_config describes a simplified wireframe position for the mockup
const FORMATS = [
  { code: 'rect',    name: 'Rectangle banner',         dim: '300x250',  cpm: 22.80, cat: 'desktop', preview: { position: 'sidebar', w: 60, h: 50 } },
  { code: 'halfpage',name: 'Half page banner',         dim: '300x600',  cpm: 34.44, cat: 'desktop', preview: { position: 'sidebar', w: 60, h: 120 } },
  { code: 'billboard',name: 'Billboard banner',        dim: '970x250',  cpm: 45.84, cat: 'desktop', preview: { position: 'top', w: 280, h: 70 } },
  { code: 'mega',    name: 'Mega billboard banner',    dim: '970x500',  cpm: 76.44, cat: 'desktop', preview: { position: 'top', w: 280, h: 140 } },
  { code: 'wallpaper',name: 'Wallpaper banner',        dim: '2x200x800',cpm: 57.24, cat: 'desktop', preview: { position: 'wallpaper' } },
  { code: 'tandem',  name: 'Tandem billboard + wallpaper', dim: '970x250 + 2x200x800', cpm: 91.80, cat: 'desktop', preview: { position: 'tandem' } },
  { code: 'fullpage',name: 'Full page branding',       dim: '2x200x800 + 970x250 + 300x600', cpm: 114.72, cat: 'desktop', preview: { position: 'fullpage' } },
  { code: 'inarticle',name: 'In article banner',       dim: '580x300',  cpm: 57.36, cat: 'desktop', preview: { position: 'inarticle', w: 180, h: 90 } },
  { code: 'canvas',  name: 'Canvas banner',            dim: '1920x1080',cpm: 95.64, cat: 'desktop', preview: { position: 'hero', w: 380, h: 180 } },
  { code: 'canvasvid',name: 'Canvas banner + video',   dim: '.mp4 16:9 do 10MB', cpm: 114.72, cat: 'desktop', preview: { position: 'hero', w: 380, h: 180 } },
  { code: 'ticker',  name: 'Ticker banner',            dim: '1200x200', cpm: 57.36, cat: 'desktop', preview: { position: 'ticker', w: 380, h: 40 } },
  { code: 'peel',    name: 'Peel away',                dim: '1920x1080 + 200x200', cpm: 114.72, cat: 'desktop', preview: { position: 'peel' } },
  { code: 'corner',  name: 'In corner',                dim: '320x250',  cpm: 56.40, cat: 'desktop', preview: { position: 'corner', w: 90, h: 70 } },
  { code: 'takeover',name: 'Take over',                dim: '500x500',  cpm: 114.72, cat: 'desktop', preview: { position: 'center', w: 180, h: 180 } },
  { code: 'preroll', name: 'Desktop pre-roll video banner', dim: '.mp4 do 10MB', cpm: 114.72, cat: 'desktop', preview: { position: 'center', w: 280, h: 160 } },
  { code: 'sponsored',name: 'Sponzorirani članak',     dim: 'naslovnica 2 dana', cpm: 1550.00, cat: 'desktop', preview: { position: 'sponsored' }, flat: true },
  // Mobile
  { code: 'm_topbot',name: 'Top/Bottom page banner',   dim: '320x100',  cpm: 23.88, cat: 'mobile', preview: { position: 'top', w: 150, h: 30, mobile: true } },
  { code: 'm_rect',  name: 'Rectangle banner',         dim: '300x250',  cpm: 35.88, cat: 'mobile', preview: { position: 'center', w: 120, h: 100, mobile: true } },
  { code: 'm_half',  name: 'Half page banner',         dim: '300x600',  cpm: 47.88, cat: 'mobile', preview: { position: 'center', w: 120, h: 240, mobile: true } },
  { code: 'm_inart', name: 'In article banner',        dim: '320x180',  cpm: 47.76, cat: 'mobile', preview: { position: 'inarticle', w: 150, h: 80, mobile: true } },
  { code: 'm_bubble',name: 'Bubble banner',            dim: '120x120',  cpm: 47.76, cat: 'mobile', preview: { position: 'bubble', w: 60, h: 60, mobile: true } },
  { code: 'm_ticker',name: 'Ticker banner',            dim: '320x100',  cpm: 59.76, cat: 'mobile', preview: { position: 'ticker', w: 150, h: 30, mobile: true } },
  { code: 'm_corner',name: 'In corner banner',         dim: '250x250',  cpm: 95.52, cat: 'mobile', preview: { position: 'corner', w: 80, h: 80, mobile: true } },
  { code: 'm_take',  name: 'Take over banner',         dim: '320x480',  cpm: 119.40, cat: 'mobile', preview: { position: 'center', w: 150, h: 220, mobile: true } },
  { code: 'm_peel',  name: 'Peel away mobile',         dim: '320x500 + 100x100', cpm: 119.40, cat: 'mobile', preview: { position: 'peel', mobile: true } },
  { code: 'm_preroll',name: 'Mobile pre-roll video banner', dim: '.mp4 do 10MB', cpm: 99.60, cat: 'mobile', preview: { position: 'center', w: 150, h: 100, mobile: true } },
];

// ── Wireframe mockup ──
function PreviewMockup({ format }) {
  const p = format.preview || {};
  const isMobile = p.mobile;
  const frameW = isMobile ? 180 : 420;
  const frameH = isMobile ? 320 : 280;
  const lorem = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum vel dolor eget nibh. ';

  const renderBanner = (style) => (
    <div className="absolute bg-red-500/80 border-2 border-red-600 flex items-center justify-center text-[9px] font-bold text-white z-10 shadow-lg"
         style={style}>
      {format.name}
    </div>
  );

  // Position-based placement
  const banners = [];
  const position = p.position;
  if (position === 'top') {
    banners.push(renderBanner({ top: 20, left: (frameW - (p.w || 280)) / 2, width: p.w || 280, height: p.h || 70 }));
  } else if (position === 'ticker') {
    banners.push(renderBanner({ top: 4, left: (frameW - (p.w || 380)) / 2, width: p.w || 380, height: p.h || 40 }));
  } else if (position === 'hero') {
    banners.push(renderBanner({ top: 50, left: (frameW - (p.w || 380)) / 2, width: p.w || 380, height: p.h || 180 }));
  } else if (position === 'sidebar') {
    banners.push(renderBanner({ top: 60, right: 12, width: p.w || 60, height: p.h || 50 }));
  } else if (position === 'inarticle') {
    banners.push(renderBanner({ top: 140, left: 16, width: p.w || 180, height: p.h || 90 }));
  } else if (position === 'corner') {
    banners.push(renderBanner({ bottom: 12, right: 12, width: p.w || 90, height: p.h || 70 }));
  } else if (position === 'bubble') {
    banners.push(renderBanner({ bottom: 16, right: 16, width: p.w || 60, height: p.h || 60, borderRadius: '50%' }));
  } else if (position === 'center') {
    banners.push(renderBanner({ top: (frameH - (p.h || 180)) / 2, left: (frameW - (p.w || 180)) / 2, width: p.w || 180, height: p.h || 180 }));
  } else if (position === 'wallpaper') {
    banners.push(renderBanner({ top: 20, left: 0, width: 40, height: frameH - 40 }));
    banners.push(renderBanner({ top: 20, right: 0, width: 40, height: frameH - 40 }));
  } else if (position === 'tandem') {
    banners.push(renderBanner({ top: 20, left: 50, width: frameW - 100, height: 60 }));
    banners.push(renderBanner({ top: 90, left: 0, width: 40, height: frameH - 110 }));
    banners.push(renderBanner({ top: 90, right: 0, width: 40, height: frameH - 110 }));
  } else if (position === 'fullpage') {
    banners.push(renderBanner({ top: 20, left: 50, width: frameW - 100, height: 50 }));
    banners.push(renderBanner({ top: 80, left: 0, width: 40, height: frameH - 100 }));
    banners.push(renderBanner({ top: 80, right: 0, width: 40, height: frameH - 100 }));
    banners.push(renderBanner({ top: 90, right: 50, width: 80, height: 140 }));
  } else if (position === 'peel') {
    banners.push(renderBanner({ top: 0, right: 0, width: 80, height: 80, clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }));
  } else if (position === 'sponsored') {
    banners.push(renderBanner({ top: 30, left: 20, right: 20, height: 60 }));
  }

  return (
    <div className="relative bg-gray-200 rounded-lg overflow-hidden shadow-inner mx-auto border-4 border-gray-700"
         style={{ width: frameW, height: frameH }}>
      <div className="absolute inset-0 p-3 text-[6px] text-gray-400 leading-tight overflow-hidden opacity-60">
        <div className="h-2 bg-gray-300 w-20 mb-2" />
        <div className="h-1 bg-gray-300 w-full mb-1" />
        <div className="h-1 bg-gray-300 w-full mb-1" />
        <div className="h-1 bg-gray-300 w-3/4 mb-3" />
        <p>{lorem.repeat(30)}</p>
      </div>
      {banners}
    </div>
  );
}

function PreviewModal({ format, onClose, onAdd }) {
  if (!format) return null;
  const typical = {
    top: 'Pojavljuje se na vrhu stranice iznad sadržaja',
    ticker: 'Pojavljuje se kao traka na vrhu stranice',
    hero: 'Velik banner istaknut na naslovnici',
    sidebar: 'Bočni banner uz sadržaj',
    inarticle: 'Unutar članka između paragrafa',
    corner: 'U donjem desnom kutu stranice',
    center: 'Overlay u sredini ekrana',
    wallpaper: 'S obje strane sadržaja',
    tandem: 'Kombinacija billboard + wallpaper',
    fullpage: 'Potpuno brendiranje stranice',
    peel: 'Peel-away efekt u kutu',
    sponsored: 'Istaknut sponzorirani članak na naslovnici',
    bubble: 'Plutajući mjehurić u kutu',
  }[format.preview?.position] || 'Standardni oglasni prostor';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">{format.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{format.dim} • {format.flat ? `${format.cpm.toFixed(2)} €/kom` : `${format.cpm.toFixed(2)} € CPM`}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={22} /></button>
        </div>
        <div className="py-4 flex justify-center"><PreviewMockup format={format} /></div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-4 mb-5">{typical}.</p>
        <button onClick={() => { onAdd(format); onClose(); }} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg flex items-center justify-center gap-2" data-testid="preview-add-to-basket">
          <ShoppingBasket size={16} /> Dodaj u košaricu
        </button>
      </div>
    </div>
  );
}

export default function AdvertisingPage() {
  const { t } = useTranslation();
  const [previewFormat, setPreviewFormat] = useState(null);
  const [basket, setBasket] = useState([]);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [processors, setProcessors] = useState({ stripe: true, paypal: true, mypos: false });
  // Pre-filled discount code/link from `/popust/:token` landing redirect.
  const [prefillDiscount, setPrefillDiscount] = useState({ code: '', token: '' });

  // Calculator
  const [calcFormat, setCalcFormat] = useState(FORMATS[2].code);
  const [impressions, setImpressions] = useState(20000);
  const [days, setDays] = useState(14);
  const [specialTargeting, setSpecialTargeting] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/payment-processors`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setProcessors(prev => ({ ...prev, ...d }));
    }).catch(() => {});
    // Success/cancel notifications from Stripe redirect
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('paid') === '1') {
      toast.success('Plaćanje uspješno! Sada možete učitati svoj medij i upravljati oglasom.');
      const sid = qs.get('sid');
      if (sid && sid !== '{CHECKOUT_SESSION_ID}') {
        // Materialise the placement on the backend, then redirect the
        // user to /moji-oglasi where they can upload their creative.
        fetch(`${API}/public/advertising/confirm?sid=${encodeURIComponent(sid)}`, { method: 'POST' })
          .then(r => r.ok ? r.json() : null)
          .then(d => {
            if (d?.placement_id) {
              setTimeout(() => { window.location.href = '/moji-oglasi?tab=ads'; }, 1500);
            }
          }).catch(() => {});
      }
    }
    if (qs.get('cancelled') === '1') toast.info('Plaćanje otkazano.');
    // Prefill from AdSlot quick-purchase: ?add=billboard&days=14&impressions=20000&slot=...
    const addCode = qs.get('add');
    if (addCode) {
      const f = FORMATS.find(x => x.code === addCode);
      if (f) {
        const d2 = parseInt(qs.get('days')) || 14;
        const imp = parseInt(qs.get('impressions')) || 20000;
        setDays(d2); setImpressions(imp);
        setBasket(b => b.find(x => x.code === f.code) ? b : [...b, { ...f, qty: 1, days: d2 }]);
        setTimeout(() => setCheckoutOpen(true), 200);
      }
    }
    // Prefill from one-time discount link landing: ?d=CODE&t=TOKEN
    const d = qs.get('d');
    const tok = qs.get('t');
    if (d || tok) {
      setPrefillDiscount({ code: (d || '').toUpperCase(), token: tok || '' });
      // Auto-open checkout so the user immediately sees their applied code
      if (d) setTimeout(() => setCheckoutOpen(true), 250);
    }
  }, []);

  const calc = useMemo(() => {
    const f = FORMATS.find(x => x.code === calcFormat) || FORMATS[0];
    const baseCpm = f.cpm;
    let gross = f.flat ? baseCpm : (impressions / 1000) * baseCpm;
    if (specialTargeting) gross *= 1.3;
    const daily = gross / Math.max(days, 1);
    return { cpm: baseCpm, gross, daily, format: f, underMin: gross < 500 };
  }, [calcFormat, impressions, days, specialTargeting]);

  function addToBasket(f) {
    setBasket(b => {
      const existing = b.find(x => x.code === f.code);
      if (existing) return b.map(x => x.code === f.code ? { ...x, qty: x.qty + 1, days: days } : x);
      return [...b, { ...f, qty: 1, days: days }];
    });
    toast.success(`${f.name} dodan u košaricu`);
  }
  function removeFromBasket(code) { setBasket(b => b.filter(x => x.code !== code)); }
  // Purchase price = CPM × days × qty. Flat formats: price × qty (days have no
  // effect — they're already a fixed-period publication).
  const lineTotal = (b) => b.flat ? b.cpm * b.qty : b.cpm * (b.days || 1) * b.qty;
  const basketTotal = basket.reduce((sum, x) => sum + lineTotal(x), 0);

  return (
    <>
      <Helmet><title>Oglašavanje — e-mina.eu</title></Helmet>
      <div className="min-h-screen bg-gray-950 text-white" data-testid="advertising-page">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">{t('advertising.title')}</h1>
          <p className="text-gray-400 mb-8">{t('advertising.subtitle')}</p>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Calculator */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="cpm-calculator">
                <div className="flex items-center gap-2 mb-4">
                  <Calculator size={18} className="text-red-500" />
                  <h2 className="text-lg font-bold">{t('advertising.calculator_title')}</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <label className="block">
                    <span className="text-xs text-gray-400">{t('advertising.format_label')}</span>
                    <select value={calcFormat} onChange={e => setCalcFormat(e.target.value)} className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm" data-testid="calc-format">
                      {FORMATS.map(f => <option key={f.code} value={f.code}>{f.name} ({f.dim})</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-400">{t('advertising.duration_label')}</span>
                    <div className="flex gap-1 mt-1">
                      {[7, 14, 30].map(d => (
                        <button key={d} onClick={() => setDays(d)} className={`flex-1 py-2 text-xs rounded ${days === d ? 'bg-red-600' : 'bg-gray-800 hover:bg-gray-700'}`} data-testid={`calc-days-${d}`}>{d}</button>
                      ))}
                      <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 1)} className="w-16 px-2 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-center" data-testid="calc-days-custom" />
                    </div>
                  </label>
                </div>
                <label className="block mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{t('advertising.impressions_label')}</span>
                    <input type="number" min={1000} max={5000000} step={1000} value={impressions}
                      onChange={e => setImpressions(Math.max(1000, Math.min(5000000, parseInt(e.target.value) || 1000)))}
                      className="w-32 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-right" data-testid="calc-impressions-input" />
                  </div>
                  <input type="range" min={1000} max={5000000} step={1000} value={impressions} onChange={e => setImpressions(parseInt(e.target.value))} className="w-full mt-2 accent-red-600" data-testid="calc-impressions-slider" />
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {[20000, 100000, 500000, 1000000, 2000000].map(p => (
                      <button key={p} onClick={() => setImpressions(p)} className={`text-xs px-3 py-1 rounded ${impressions === p ? 'bg-red-600' : 'bg-gray-800 hover:bg-gray-700'}`} data-testid={`calc-imp-${p}`}>
                        {(p / 1000).toLocaleString()}k
                      </button>
                    ))}
                  </div>
                </label>
                <label className="flex items-center gap-2 mb-5 text-sm">
                  <input type="checkbox" checked={specialTargeting} onChange={e => setSpecialTargeting(e.target.checked)} className="accent-red-600" data-testid="calc-targeting" />
                  {t('advertising.targeting_label')}
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-gray-800">
                  <div><p className="text-[10px] text-gray-500 uppercase">{t('advertising.cpm_label')}</p><p className="text-lg font-bold">€ {calc.cpm.toFixed(2)}</p></div>
                  <div><p className="text-[10px] text-gray-500 uppercase">{t('advertising.impressions_short')}</p><p className="text-lg font-bold">{impressions.toLocaleString()}</p></div>
                  <div><p className="text-[10px] text-gray-500 uppercase">{t('advertising.gross_price')}</p><p className={`text-lg font-bold ${calc.underMin ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="calc-gross">€ {calc.gross.toFixed(2)}</p></div>
                  <div><p className="text-[10px] text-gray-500 uppercase">{t('advertising.daily_price')}</p><p className="text-lg font-bold">€ {calc.daily.toFixed(2)}</p></div>
                </div>
                {calc.underMin && <p className="mt-3 text-xs text-amber-400" data-testid="calc-min-warning">⚠ {t('advertising.min_warning')}</p>}
              </div>

              {/* Format tables */}
              {['desktop', 'mobile'].map(cat => (
                <div key={cat}>
                  <h3 className="text-lg font-bold text-red-500 mb-4 uppercase">{cat}</h3>
                  <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800">
                    <table className="w-full text-sm" data-testid={`price-table-${cat}`}>
                      <thead className="bg-gray-950 text-gray-400 text-xs">
                        <tr>
                          <th className="text-left px-4 py-3">NAZIV FORMATA</th>
                          <th className="text-left px-4 py-3">DIMENZIJE</th>
                          <th className="text-right px-4 py-3">CPM (€)</th>
                          <th className="px-4 py-3 w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {FORMATS.filter(f => f.cat === cat).map(f => (
                          <tr key={f.code} className="border-t border-gray-800 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{f.name}</td>
                            <td className="px-4 py-3 text-gray-500">{f.dim}</td>
                            <td className="px-4 py-3 text-right text-red-400 font-semibold">{f.cpm.toFixed(2)} €</td>
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => setPreviewFormat(f)} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300" data-testid={`preview-${f.code}`}>
                                <Eye size={12} /> {t('advertising.preview_btn')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            {/* Basket sidebar */}
            <aside className="lg:sticky lg:top-20 self-start bg-gray-900 rounded-xl border border-gray-800 p-5 h-fit" data-testid="advertising-basket">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingBasket size={16} className="text-red-500" />
                <h3 className="font-bold uppercase tracking-wider text-sm">{t('advertising.basket_title')} ({basket.length})</h3>
              </div>
              {basket.length === 0 ? (
                <p className="text-xs text-gray-500 py-6 text-center">{t('advertising.basket_empty')}</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {basket.map(b => (
                    <div key={b.code} className="flex items-center justify-between text-xs bg-gray-950 p-2 rounded" data-testid={`basket-item-${b.code}`}>
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium">{b.name}</p>
                        <p className="text-gray-500">{b.qty} × € {b.cpm.toFixed(2)} {b.flat ? '/kom' : `× ${b.days || 1} dana`}</p>
                        <p className="text-emerald-400 font-semibold">€ {lineTotal(b).toFixed(2)}</p>
                      </div>
                      <button onClick={() => removeFromBasket(b.code)} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-3 border-t border-gray-800 flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400">{t('advertising.basket_total')}</span>
                <span className="font-bold text-emerald-400">€ {basketTotal.toFixed(2)}</span>
              </div>
              {processors.stripe && (
                <button disabled={basket.length === 0} onClick={() => setCheckoutOpen(true)}
                        className="w-full py-2.5 mb-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="buy-now-btn">
                  <CreditCard size={14} /> {t('advertising.buy_now')}
                </button>
              )}
              <button disabled={basket.length === 0} onClick={() => setInquiryOpen(true)} className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded disabled:opacity-50 flex items-center justify-center gap-2" data-testid="request-quote-btn">
                <Send size={14} /> {t('advertising.request_quote')}
              </button>
              {!processors.stripe && (
                <p className="text-[10px] text-amber-400 mt-2 text-center">Kupnja karticom privremeno isključena. Molimo pošaljite upit.</p>
              )}
            </aside>
          </div>
        </div>

        <PreviewModal format={previewFormat} onClose={() => setPreviewFormat(null)} onAdd={addToBasket} />
        {inquiryOpen && <InquiryModal basket={basket} onClose={() => setInquiryOpen(false)} />}
        {checkoutOpen && <CheckoutModal basket={basket} impressions={impressions} days={days} prefillDiscount={prefillDiscount} onClose={() => setCheckoutOpen(false)} />}
      </div>
    </>
  );
}

function InquiryModal({ basket, onClose }) {
  const prefill = basket.map(b => `• ${b.name} (${b.dim}) × ${b.qty} — € ${(b.cpm * b.qty).toFixed(2)}`).join('\n');
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: `Zanima me zakup oglasnog prostora:\n${prefill}\n\n` });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/ad-inquiry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, submitted_at: new Date().toISOString() }),
      });
      if (r.ok) setSent(true); else toast.error('Greška');
    } catch { toast.error('Greška'); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="inquiry-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-xl w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">Zatraži ponudu</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={22} /></button>
        </div>
        {sent ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-3"><Check className="text-emerald-600" size={24} /></div>
            <p className="font-semibold text-gray-900 dark:text-gray-100 dark:text-white">Hvala! Upit je zaprimljen.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="inquiry-form">
            <input required placeholder="Ime i prezime" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border rounded text-sm" data-testid="inquiry-name" />
            <input required type="email" placeholder="E-mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded text-sm" data-testid="inquiry-email" />
            <input placeholder="Tvrtka (opcionalno)" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="w-full px-3 py-2 border rounded text-sm" />
            <input placeholder="Telefon (opcionalno)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border rounded text-sm" />
            <textarea required rows={8} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="w-full px-3 py-2 border rounded text-sm font-mono" data-testid="inquiry-message" />
            <button type="submit" disabled={submitting} className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Pošalji upit
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function CheckoutModal({ basket, impressions, days, prefillDiscount, onClose }) {
  const [form, setForm] = useState({ email: '', company: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [tiers, setTiers] = useState([]);
  const [discountCode, setDiscountCode] = useState(prefillDiscount?.code || '');
  const [discountToken] = useState(prefillDiscount?.token || '');
  const [discount, setDiscount] = useState(null); // { code, discount_eur, total_eur, free }
  const [validating, setValidating] = useState(false);
  useEffect(() => {
    fetch(`${API}/public/ad-discount-tiers`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.tiers) setTiers(d.tiers);
    }).catch(() => {});
  }, []);
  // Auto-validate prefilled code from one-time discount-link landing
  useEffect(() => {
    if (prefillDiscount?.code) {
      // Run validation once when modal opens with a prefilled code
      setTimeout(() => { applyCode(); }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const basketTotal = basket.reduce((sum, x) => sum + x.cpm * x.qty, 0);
  const grossEstimate = basketTotal * (impressions / 1000);
  const activeTier = [...tiers].sort((a, b) => a.min_days - b.min_days).reduce((best, t) => (days >= (t.min_days || 0) ? t : best), null);
  const discountPct = activeTier ? parseFloat(activeTier.discount_pct || 0) : 0;
  const tierAdjusted = grossEstimate * (1 - discountPct / 100);
  const finalEstimate = discount ? Math.max(0, tierAdjusted - discount.discount_eur) : tierAdjusted;

  async function applyCode() {
    if (!discountCode.trim()) { setDiscount(null); return; }
    setValidating(true);
    try {
      const r = await fetch(`${API}/public/marketing-discount/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode, subtotal_eur: tierAdjusted, scope: 'advertising' }),
      });
      const d = await r.json();
      if (!r.ok) { setDiscount(null); toast.error(d?.detail || 'Nevažeći kod'); }
      else { setDiscount(d); toast.success(d.free ? 'Kod primijenjen — narudžba je BESPLATNA!' : `Kod primijenjen: −€${d.discount_eur.toFixed(2)}`); }
    } catch (e) { setDiscount(null); toast.error('Nevažeći kod'); }
    finally { setValidating(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.email) { setErr('E-mail je obavezan'); return; }
    setErr('');
    setSubmitting(true);
    try {
      const payload = {
        items: basket.map(b => ({ code: b.code, name: b.name, dim: b.dim, cpm: b.cpm, qty: b.qty })),
        impressions, days,
        email: form.email, company: form.company, phone: form.phone,
        discount_code: discount?.code || null,
        discount_link_token: discountToken || null,
        success_url: `${window.location.origin}/oglasavanje?paid=1&sid={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/oglasavanje?cancelled=1`,
      };
      const r = await fetch(`${API}/public/advertising/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok && d.url) {
        window.location.href = d.url;
      } else {
        setErr(d?.detail || 'Greška pri kreiranju naloga za plaćanje');
      }
    } catch (ex) {
      setErr(String(ex?.message || ex));
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="checkout-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-xl w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">Kupi odmah</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={22} /></button>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mb-4 text-sm">
          <p className="font-bold text-gray-900 dark:text-gray-100 mb-1">Sažetak narudžbe</p>
          {basket.map(b => (
            <div key={b.code} className="flex justify-between text-xs text-gray-700 dark:text-gray-300">
              <span>{b.name} · {b.qty}×</span>
              <span>€ {(b.cpm * b.qty).toFixed(2)} {b.flat ? '/kom' : 'CPM'}</span>
            </div>
          ))}
          {discountPct > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-700 flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="checkout-long-term-badge">
              <span>Rabat za dugoročni zakup ({days} dana)</span>
              <span>-{discountPct}%</span>
            </div>
          )}
          {discount && (
            <div className="mt-1 flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="checkout-discount-applied">
              <span>Promo kod {discount.code}</span>
              <span>{discount.free ? '−100% (FREE)' : `−€ ${discount.discount_eur.toFixed(2)}`}</span>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-700 flex justify-between text-sm font-bold text-gray-900 dark:text-gray-100">
            <span>Procjena (za {impressions.toLocaleString()} impresija)</span>
            <span data-testid="checkout-estimate">€ {finalEstimate.toFixed(2)}</span>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">* Stvarni trošak izračunava se na temelju pakiranja CPM/flat formata pri plaćanju (Stripe).</p>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="checkout-form">
          <input required type="email" placeholder="E-mail za račun" value={form.email}
                 onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                 className="w-full px-3 py-2 border rounded text-sm dark:bg-gray-950 dark:border-gray-700 dark:text-white"
                 data-testid="checkout-email" />
          <input placeholder="Tvrtka (opcionalno)" value={form.company}
                 onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                 className="w-full px-3 py-2 border rounded text-sm dark:bg-gray-950 dark:border-gray-700 dark:text-white" />
          <input placeholder="Telefon (opcionalno)" value={form.phone}
                 onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                 className="w-full px-3 py-2 border rounded text-sm dark:bg-gray-950 dark:border-gray-700 dark:text-white" />
          <div className="flex gap-2">
            <input placeholder="Promo kod (opcionalno)" value={discountCode}
                   onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                   className="flex-1 px-3 py-2 border rounded text-sm font-mono dark:bg-gray-950 dark:border-gray-700 dark:text-white"
                   data-testid="checkout-discount-code" />
            <button type="button" onClick={applyCode} disabled={validating || !discountCode}
                    className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded disabled:opacity-50"
                    data-testid="checkout-apply-code">
              {validating ? <Loader2 className="animate-spin" size={14} /> : 'Primijeni'}
            </button>
          </div>
          {discountToken && (
            <p className="text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="checkout-one-time-link-badge">
              Jednokratni link je aktivan — popust će biti primijenjen na ovu kupnju.
            </p>
          )}
          {err && <p className="text-xs text-red-500" data-testid="checkout-error">{err}</p>}
          <button type="submit" disabled={submitting}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="checkout-submit">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
            {discount?.free ? 'Završi (BESPLATNO)' : 'Nastavi na Stripe'}
          </button>
          <p className="text-[10px] text-center text-gray-500">Sigurno plaćanje: Stripe, VISA, Mastercard, Apple Pay, Google Pay.</p>
        </form>
      </div>
    </div>
  );
}
